import {
  applyDecisionEvent,
  AttachmentFollowUp,
  AttachmentType,
  BRSigner,
  canApplyDecision,
  DbotEvent,
  DbotEventDispatcher,
  DecisionFollowUp,
  DecisionItem,
  DocumentItem,
  downloadSignedDocumentEvent,
  downloadSignedSubscriptionForms,
  findStateForDecision,
  FollowUp,
  FollowUpType,
  getSigningStatusEvent,
  getSubscriptionStatusEvent,
  isDecisionApplied,
  isFollowUp,
  makeCountryRadioFieldModel,
  OpenReviewDialogAction,
  OpenSendForReviewDialogAction,
  OpenSubscriptionFormBatchPreviewDialogAction,
  ProjectContext,
  ProjectDocumentStatus,
  refreshProjectStatusEvent,
  reopenDocumentEvent,
  sendToBREvent,
  updateFollowUpStatusEvent,
  uploadAttachmentsEvent,
} from "../common/commonDomain";
import { makeAutoObservable } from "mobx";
import {
  canBeSentForVoting,
  ConfirmSendToBRDialogModel,
  DecisionAction,
  DecisionListItem,
  DialogState,
  getBoardRecipients,
  getBoardRecipientsEligibleForPowerOfAttorney,
  getBoardSignersInfo,
  getShareholderRecipients,
  hasInvalidRecipients,
  noDialog,
  ReopenLockedDocumentDialogModel,
  ReviewDocumentSigningDialogModel,
  ReviewSendNoticeOfBoardMeetingDialogModel,
  ReviewSendNoticeOfGeneralMeetingDialogModel,
  ReviewSendSubscriptionFormDialogModel,
  SendForReviewDialogModel,
  ViewDocumentSigningStatusDialogModel,
  ViewDocumentViewingStatusDialogModel,
  ViewSubscriptionFormBatchPreviewModel,
  ViewSubscriptionStatusDialogModel,
} from "./summaryDomain";
import { dNumberValidator, norwegianPersonalIdValidator } from "../../utils/other";
import { emailValidator, isOk, SharepointSettingsForDocument } from "../../common/common";
import { DocumentSigningStatus } from "../../document/documentApi";
import { compact } from "lodash";
import {
  ShareAllocationRoundsModel,
  SubscribersStatusModel,
} from "../increaseOfShareCapital/increaseOfShareCapitalDomain";
import { getFullName, sortedBoardAndCEO } from "../../utils/Roles";
import { DocumentType } from "../../common/documents";
import { createEmailField, RadioFieldModel, StringFieldModel } from "../../common/commonForms";
import { isCountryCodeNorway, isCountryCodeSweden, norway } from "../../utils/countries";
import { hasShares } from "../../stores/ShareholdersStore";
import { httpGet, httpPost } from "../../events/eventsDomain";
import {
  SharepointContentInDrive,
  SharepointContext,
  SharepointDrive,
} from "../../sharepoint/sharepointDomain";
import { Language } from "../../i18n/languages";

export class SummaryController {
  readonly dispatch: DbotEventDispatcher;
  readonly context: ProjectContext;
  isRefreshingSigningStatuses = false;
  dialogState: DialogState = noDialog();
  followUps: Record<string, DecisionFollowUp> = {};
  isApplying: Set<string> = new Set();
  isSendingToBR: Set<string> = new Set();

  constructor(
    dispatch: DbotEventDispatcher,
    context: ProjectContext,
    followUps: Record<string, DecisionFollowUp>,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.dispatch = dispatch;
    this.context = context;
    this.followUps = followUps;
  }

  toDocumentListItem(d: DocumentItem): DocumentListItem {
    return {
      document: d,
      signingStatus: this.getSigningStatusForDocument(d),
      numberOfSigners: this.getDocumentStatus(d)?.numberOfSigners ?? 0,
      numberOfSignatures: this.getDocumentStatus(d)?.numberOfSignatures ?? 0,
      isRefreshingSigningStatus: this.isRefreshingSigningStatuses,
      sendingTime: this.getDocumentSendingTime(d),
      showDocumentListItem:
        d.type !== DocumentType.articlesofassociation && d.type !== DocumentType.subscription_form,
    };
  }

  get decisionsForApply() {
    const decisionsByCompany: Array<[orgNo: string, DecisionItem[]]> = [
      [this.context.orgNo, this.context.currentDecisions],
      ...Object.entries(this.context.batchData).map<[string, DecisionItem[]]>(
        ([orgNo, batchData]) => [
          orgNo,
          this.context.currentDecisions.map((d) => ({
            ...d,
            decisionId: batchData.decisionMapping[d.decisionId].targetDecisionId,
          })),
        ],
      ),
    ];

    return decisionsByCompany.flatMap(([orgNo, decisions]) =>
      compact(
        decisions
          .filter((d) => d.decisionId in this.followUps)
          .map((d, i, decisions) => {
            const decisionFollowUps: DecisionFollowUp = this.followUps[d.decisionId];
            const previousDecisionFollowUps =
              i > 0 ? this.followUps[decisions[i - 1].decisionId] : undefined;
            const isApplying = this.isApplying.has(d.decisionId);
            const isSendingToBR = this.isSendingToBR.has(d.decisionId);
            const isPreviousDecisionApplied =
              previousDecisionFollowUps === undefined
                ? true
                : !canApplyDecision(previousDecisionFollowUps.followUps) ||
                  isDecisionApplied(previousDecisionFollowUps.followUps);

            if (!decisionFollowUps) {
              return undefined;
            }

            return new DecisionListItem({
              orgNo,
              decision: d,
              followUps: decisionFollowUps.followUps,
              isApplying,
              isSendingToBR,
              isEnabled: decisionFollowUps.isEnabled,
              isPreviousDecisionApplied: isPreviousDecisionApplied,
              uploadedDecisionAttachments: decisionFollowUps.uploadedDecisionAttachments,
              fileUploadConfig: this.context.fileUploadConfig,
              documents: this.context.currentDocuments.map((document) =>
                this.toDocumentListItem(document),
              ),
            });
          }),
      ),
    );
  }

  updateSubscriptionFormFollowUp(documentId: string) {
    for (const [decisionId, decisionFollowUp] of Object.entries(this.followUps)) {
      if (
        decisionFollowUp.followUps.find(
          (d) => d.data.type === FollowUpType.subscriptionForm && d.data.documentId === documentId,
        )
      ) {
        this.dispatch(
          updateFollowUpStatusEvent(decisionId, {
            type: FollowUpType.subscriptionForm,
            documentId: documentId,
          }),
        );
      }
    }
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/reopen-document-result":
        if (
          this.dialogState.type === "reopenLockedDocument" ||
          this.dialogState.type === "viewDocumentSigningStatus"
        ) {
          this.dialogState.model.setReopenResult(event.reopenResult);
          if (isOk(event.reopenResult)) {
            this.dispatch(refreshProjectStatusEvent());
            this.updateSubscriptionFormFollowUp(event.documentId);
            this.isRefreshingSigningStatuses = true;
            this.closeDialog();
          }
        }
        break;
      case "dbot/refresh-project-status-result":
        this.isRefreshingSigningStatuses = false;
        break;
      case "dbot/update-follow-up-status-result":
      case "dbot/fetch-decision-status-result":
        this.followUps = event.data.followUps;
        break;
      case "dbot/apply-decision-result":
        this.isApplying.delete(event.decisionId);
        break;
      case "dbot/send-to-br-result":
        this.isSendingToBR.delete(event.decisionId);
        if (this.dialogState.type === "confirmSendToBR") {
          this.dialogState.model.handleEvent(event);
        }
        break;
      case "dbot/upload-attachments-result":
      case "dbot/delete-attachment-result":
        if (isFollowUp(event.source) && event.reference !== undefined) {
          this.dispatch(updateFollowUpStatusEvent(event.reference, event.source.data));
        }
        break;
      case "dbot/get-signing-status-result":
      case "dbot/resend-document-for-signing-result":
        if (this.dialogState.type === "viewDocumentSigningStatus") {
          this.dialogState.model.setSigningStatusResult(event.result);
        }
        break;
      case "dbot/get-subscription-status-result":
        if (this.dialogState.type === "viewSubscriptionStatus") {
          this.dialogState.model.setSubscriptionStatusResult(event.result);
        }
        break;
      case "dbot/download-document":
        this.dispatch(downloadSignedDocumentEvent(event.document.documentId, event.document.type));
        break;
      case "dbot/download-subscription-forms":
        this.dispatch(downloadSignedSubscriptionForms(event.document.documentId));
        break;
      case "dbot/validate-project-action":
        if (
          this.context.currentDecisions.length > 0 ||
          this.context.currentDocuments.every((x) => x.type === DocumentType.custom_document)
        ) {
          this.dispatch({
            type: "dbot/validate-project",
            onSuccess: event.successAction,
          });
        } else {
          this.dispatch({
            type: "dbot/project-validation-failed",
            validationError: { key: "dbot.status-no-decisions.error" },
          });
        }
        break;
      case "dbot/openSubscriptionFormBatchPreviewDialog":
        this.openSubscriptionFormBatchPreviewDialog(event);
        break;
      case "dbot/open-review-dialog":
        this.openReviewDialog(event);
        break;
      case "dbot/open-send-for-review-dialog":
        this.openSendForReviewDialog(
          event,
          canBeSentForVoting(this.context.currentDecisions, event.document),
        );
        break;
      case "dbot/open-signing-status-dialog":
        this.openSigningStatusDialog(event);
        break;
      case "dbot/open-viewing-status-dialog":
        this.dialogState = {
          type: "viewDocumentViewingStatus",
          model: new ViewDocumentViewingStatusDialogModel({
            document: event.document,
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
          }),
        };
        this.dialogState.model.init();
        break;
      case "dbot/refresh-signing-status":
        this.dispatch(refreshProjectStatusEvent());
        this.isRefreshingSigningStatuses = true;
        break;
      case "dbot/open-subscription-status":
        this.openSubscriptionStatusDialog(event);
        break;
      case "dbot/open-increase-of-share-capital-subscribers-status":
        this.dialogState = {
          type: "viewIncreaseOfShareCapitalSubscribersStatus",
          model: {
            type: "status",
            subscriberModel: new SubscribersStatusModel(
              this.dispatch,
              this.closeDialog,
              this.context.orgNo,
              this.context.shareholderData,
              this.context.documentLanguages,
              this.context.isLocked || event.isLocked,
              findStateForDecision(this.context.inputStates, event.decisionId),
            ),
          },
        };
        this.dialogState.model.subscriberModel.init();
        break;
      case "dbot/open-increase-of-share-capital-subscribers-allocation":
        this.dialogState = {
          type: "viewIncreaseOfShareCapitalSubscribersAllocation",
          model: {
            type: "allocation",
            subscriberModel: new ShareAllocationRoundsModel(
              event.decisionId,
              this.dispatch,
              this.context.shareholderData.shareholders,
              this.closeDialog,
              findStateForDecision(this.context.inputStates, event.decisionId),
            ),
          },
        };
        this.dialogState.model.subscriberModel.init();
        break;
      case "dbot/unlock-document":
        this.openUnlockDocumentDialog(event.documentId);
        break;
      case "dbot/applyDecisionAction":
        this.dispatch(applyDecisionEvent(event.decision.decisionId));
        this.isApplying.add(event.decision.decisionId);
        break;
      case "dbot/confirm-send-to-br":
        this.openConfirmSendToBRDialog(event.decisionId, event.brSigners);
        break;
      default:
        switch (this.dialogState.type) {
          case "reviewDocumentSigning":
          case "reviewSendNoticeOfBoardMeeting":
          case "reviewSendNoticeOfGeneralMeeting":
          case "reviewSendSubscriptionForm":
          case "viewDocumentViewingStatus":
          case "sendForReview":
            this.dialogState.model.handleEvent(event);
            break;
          case "viewIncreaseOfShareCapitalSubscribersStatus":
          case "viewIncreaseOfShareCapitalSubscribersAllocation":
            this.dialogState.model.subscriberModel.handleEvent(event);
            break;
          case "viewSubscriptionFormBatchPreview":
            if (event.type === "dbot/fetch-increase-of-share-capital-subscribers-status-result") {
              this.dialogState.model.setSubscribers(event.data);
            }
            break;
        }
    }
  }

  private openUnlockDocumentDialog(documentId: string) {
    this.dialogState = {
      type: "reopenLockedDocument",
      model: new ReopenLockedDocumentDialogModel({
        onSubmit: () => this.dispatch(reopenDocumentEvent(documentId)),
        onCancel: this.closeDialog,
      }),
    };
  }

  getDocumentStatus(document: DocumentItem): ProjectDocumentStatus | undefined {
    return this.context.documentStatuses.find((x) => x.documentId === document.documentId);
  }

  getSigningStatusForDocument(document: DocumentItem): DocumentSigningStatus {
    return this.getDocumentStatus(document)?.signingStatus ?? DocumentSigningStatus.UNKNOWN;
  }

  getDocumentSendingTime(document: DocumentItem): string | undefined {
    return this.getDocumentStatus(document)?.sentAt;
  }

  closeDialog() {
    this.dialogState = noDialog();
  }

  openReviewDialog(action: OpenReviewDialogAction) {
    const sharepointContext: SharepointContext = {
      company: this.context.companyInfo.company,
      user: this.context.user,
      enabled: this.context.isSharepointEnabled,
      documentType: action.document.type,
      loadSharepointDrives: this.loadSharepointDrives,
      loadContentInSharepointDrive: this.loadContentInSharepointDrive,
      saveSharepointDestinationSettings: this.saveSharepointDestinationSettings(
        action.document.type,
      ),
      settings: findSharepointSettings(this.context, action),
    };
    switch (action.document.type) {
      case DocumentType.notice_board_meeting:
        this.dialogState = {
          type: "reviewSendNoticeOfBoardMeeting",
          model: new ReviewSendNoticeOfBoardMeetingDialogModel({
            document: action.document,
            recipients: getBoardRecipients(this.context),
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
            subscriptionTypeModel: this.context.subscriptionTypeModel,
            title: action.label,
            emailBody: action.emailBody ?? {
              key: "dbot.send-for-viewing.board-meeting.email-body",
              params: { name: this.context.companyInfo.company.name },
            },
            aiClient: this.context.aiClient,
            isResend: action.isResend,
            hasAllSocialSecurityNumbers: hasAllBoardMemberSSNs(this.context),
            hasExtraParticipants: hasExtraParticipants(this.context),
          }),
        };
        break;
      case DocumentType.notice_general_meeting:
        this.dialogState = {
          type: "reviewSendNoticeOfGeneralMeeting",
          model: new ReviewSendNoticeOfGeneralMeetingDialogModel({
            companyName: this.context.companyInfo.company.name,
            document: action.document,
            hasInvalidCompanyRoleRecipients: hasInvalidRecipients(
              getBoardRecipientsEligibleForPowerOfAttorney(this.context),
            ),
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
            subscriptionTypeModel: this.context.subscriptionTypeModel,
            shareholderRecipients: getShareholderRecipients(
              this.context.shareholderData.shareholders,
            ),
            emailBody: {
              key: "dbot.send-for-viewing.general-meeting.email-body",
              params: { name: this.context.companyInfo.company.name },
            },
            aiClient: this.context.aiClient,
            documentStatus: this.getDocumentStatus(action.document),
            isMissingShareholderSSN: isMissingShareholderSSN(this.context),
            hasForeignShareholders: hasForeignShareholders(this.context),
          }),
        };
        break;
      case DocumentType.subscription_form:
        this.dialogState = {
          type: "reviewSendSubscriptionForm",
          model: new ReviewSendSubscriptionFormDialogModel({
            document: action.document,
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
            subscriptionTypeModel: this.context.subscriptionTypeModel,
            aiClient: this.context.aiClient,
            user: this.context.user,
          }),
        };
        this.dialogState.model.init();
        break;
      case DocumentType.board_meeting:
        this.dialogState = {
          type: "reviewDocumentSigning",
          model: new ReviewDocumentSigningDialogModel({
            orgNo: this.context.orgNo,
            document: action.document,
            signers: action.signers ?? [],
            documents: action.documents ?? [],
            companies: action.companies ?? [],
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
            subscriptionTypeModel: this.context.subscriptionTypeModel,
            infoMessage: getBoardSignersInfo(this.context),
            showUnsupportedAttachmentWarning: action.showUnsupportedAttachmentWarning,
            aiClient: this.context.aiClient,
            emailBody: {
              key: "dbot.send-for-signing.email-body",
              params: {
                senderName: getFullName(this.context.user),
                fileName: action.document.title,
              },
            },
            user: this.context.user,
            isBatchProject: this.context.isBatchProject,
            sharepointContext,
          }),
        };
        break;
      case DocumentType.general_meeting:
        this.dialogState = {
          type: "reviewDocumentSigning",
          model: new ReviewDocumentSigningDialogModel({
            orgNo: this.context.orgNo,
            document: action.document,
            signers: action.signers ?? [],
            documents: action.documents ?? [],
            companies: action.companies ?? [],
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
            subscriptionTypeModel: this.context.subscriptionTypeModel,
            showUnsupportedAttachmentWarning: action.showUnsupportedAttachmentWarning,
            aiClient: this.context.aiClient,
            emailBody: {
              key: "dbot.send-for-signing.email-body",
              params: {
                senderName: getFullName(this.context.user),
                fileName: action.document.title,
              },
            },
            user: this.context.user,
            isBatchProject: this.context.isBatchProject,
            sharepointContext,
          }),
        };
        break;
      case DocumentType.custom_document:
        this.dialogState = {
          type: "reviewDocumentSigning",
          model: new ReviewDocumentSigningDialogModel({
            orgNo: this.context.orgNo,
            document: action.document,
            signers: action.signers ?? [],
            documents: action.documents ?? [],
            companies: action.companies ?? [],
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
            subscriptionTypeModel: this.context.subscriptionTypeModel,
            showUnsupportedAttachmentWarning: action.showUnsupportedAttachmentWarning,
            aiClient: this.context.aiClient,
            emailBody: {
              key: "dbot.send-for-signing.email-body",
              params: {
                senderName: getFullName(this.context.user),
                fileName: action.document.title,
              },
            },
            user: this.context.user,
            isBatchProject: this.context.isBatchProject,
            sharepointContext,
          }),
        };
        break;
    }
  }

  loadSharepointDrives(): Promise<SharepointDrive[]> {
    return new Promise((resolve, reject) => {
      this.dispatch([
        httpGet({
          url: `/sharepoint/${this.context.orgNo}/drives`,
          onSuccess: (res) => {
            resolve(res.data);
            return [];
          },
          onFailure: () => {
            reject(new Error("Failed to load drives"));
            return [];
          },
        }),
      ]);
    });
  }

  loadContentInSharepointDrive(
    driveID: string,
    parentID: string | undefined,
  ): Promise<SharepointContentInDrive[]> {
    return new Promise((resolve, reject) => {
      this.dispatch([
        httpGet({
          url: `/sharepoint/${this.context.orgNo}/drives/${driveID}${parentID ? `/items/${parentID}` : ""}`,
          onSuccess: (res) => {
            resolve(res.data);
            return [];
          },
          onFailure: () => {
            reject(new Error("Failed to load content in drive"));
            return [];
          },
        }),
      ]);
    });
  }

  saveSharepointDestinationSettings(
    documentType: DocumentType,
  ): (destination?: SharepointContentInDrive) => Promise<void> {
    return (destination?: SharepointContentInDrive) =>
      new Promise((resolve, reject) => {
        this.dispatch([
          httpPost({
            url: `/sharepoint/${this.context.orgNo}/document-settings`,
            data: {
              destinationFolderId: destination?.id,
              destinationFolderName: destination?.name,
              driveId: destination?.parentReference.driveId ?? "",
              documentType,
            },
            onSuccess: (res) => {
              resolve(res.data);
              return [];
            },
            onFailure: () => {
              reject(new Error("Failed to save sharepoint settings for document"));
              return [];
            },
          }),
        ]);
      });
  }

  openSendForReviewDialog(
    { messageTemplate, document, recipients }: OpenSendForReviewDialogAction,
    canBeSentForVoting: boolean,
  ) {
    this.dialogState = {
      type: "sendForReview",
      model: new SendForReviewDialogModel({
        baseUrl: window.location.href,
        orgNo: this.context.orgNo,
        projectId: this.context.projectId,
        template: messageTemplate,
        document: document,
        dispatch: this.dispatch,
        closeDialog: this.closeDialog,
        roles: sortedBoardAndCEO(this.context.companyInfo.roles),
        canBeSentForVoting: canBeSentForVoting,
        recipients: recipients,
        context: this.context,
      }),
    };
  }

  openSigningStatusDialog({ document }: { document: DocumentItem }) {
    this.dialogState = {
      type: "viewDocumentSigningStatus",
      model: new ViewDocumentSigningStatusDialogModel({
        documentId: document.documentId,
        documentType: document.type,
        title: document.title,
        resendDocumentForSigning: (signedDocumentId, data) =>
          this.dispatch({
            type: "dbot/resend-document-for-signing",
            documentId: document.documentId,
            signedDocumentId,
            ...data,
          }),
        getSigningStatus: () => this.dispatch(getSigningStatusEvent(document.documentId)),
        downloadSignedDocument: () =>
          this.dispatch(downloadSignedDocumentEvent(document.documentId, document.type)),
        closeDialog: this.closeDialog,
        confirmDeleteSignatureRequest: () =>
          this.dispatch(reopenDocumentEvent(document.documentId)),
      }),
    };
    this.dialogState.model.init();
  }

  openSubscriptionStatusDialog({ document }: { document: DocumentItem }) {
    this.dialogState = {
      type: "viewSubscriptionStatus",
      model: new ViewSubscriptionStatusDialogModel({
        documentId: document.documentId,
        title: document.title,
        getSubscriptionStatus: () => this.dispatch(getSubscriptionStatusEvent(document.documentId)),
        closeDialog: this.closeDialog,
      }),
    };
    this.dialogState.model.init();
  }

  openSubscriptionFormBatchPreviewDialog({
    title,
    filePreview,
    successAction,
  }: OpenSubscriptionFormBatchPreviewDialogAction) {
    this.dialogState = {
      type: "viewSubscriptionFormBatchPreview",
      model: new ViewSubscriptionFormBatchPreviewModel({
        title,
        filePreview,
        context: this.context,
        closeDialog: this.closeDialog,
      }),
    };
    this.dispatch(successAction);
  }

  openConfirmSendToBRDialog(decisionId: string, brSigners: BRSigner[]) {
    const onSubmit = (signer: BRSigner) => this.dispatch(sendToBREvent(decisionId, signer));

    this.dialogState = {
      type: "confirmSendToBR",
      model: new ConfirmSendToBRDialogModel(decisionId, brSigners, onSubmit, this.closeDialog),
    };
  }
}

export type DocumentListItem = {
  document: DocumentItem;
  signingStatus: DocumentSigningStatus;
  numberOfSigners: number;
  numberOfSignatures: number;
  isRefreshingSigningStatus: boolean;
  sendingTime: string | undefined;
  showDocumentListItem: boolean;
};

export type IdentificationForPerson = {
  id?: string;
  companyRoleId?: number;
  name: string;
  nationality?: string;
  identityNumber: string;
  dnr?: string;
  email?: string;
};

export class IdentificationForPersonForm {
  data: IdentificationForPerson;
  identityNumberModel: StringFieldModel;
  emailModel: StringFieldModel;
  name: string;

  nationality: RadioFieldModel<{ code: string; name: string }>;
  dnr: StringFieldModel;

  constructor(person: IdentificationForPerson, locale: Language) {
    this.data = person;
    this.identityNumberModel = new StringFieldModel({
      value: person.identityNumber,
      required: person.id === undefined || person.nationality === norway,
      validator: norwegianPersonalIdValidator,
    });
    this.emailModel = new StringFieldModel({
      value: person.email ?? "",
      required: this.requiresEmail,
      validator: emailValidator,
    });
    this.name = person.name;

    this.nationality = makeCountryRadioFieldModel(locale, person.nationality);
    this.dnr = new StringFieldModel({
      value: person.dnr,
      required: person.id !== undefined && person.nationality !== norway,
      validator: dNumberValidator,
    });
  }

  setNationality(value: { code: string; name: string }) {
    this.nationality.setValue(value);
    this.dnr.setRequired(value.code !== norway);
    this.identityNumberModel.setRequired(value.code === norway);
  }

  get requiresEmail() {
    return this.data.companyRoleId === undefined;
  }

  get showNationality() {
    return this.data.id !== undefined;
  }

  get showDnr() {
    return this.showNationality && this.nationality.value.code !== norway;
  }

  toJSON() {
    return this.data.id
      ? {
          id: this.data.id,
          companyRoleId: this.data.companyRoleId,
          name: this.name,
          nationality: this.nationality.value.code,
          socialSecurityNumber: this.identityNumberModel.validatedValue,
          dnr: this.dnr.validatedValue,
          email: this.emailModel.validatedValue,
        }
      : {
          name: this.name,
          identityNumber: this.identityNumberModel.validatedValue,
          email: this.emailModel.validatedValue,
        };
  }

  get isValid() {
    return this.identityNumberModel.isValid && this.emailModel.isValid && this.dnr.isValid;
  }

  validate(): boolean {
    this.identityNumberModel.validate();
    this.emailModel.validate();
    this.dnr.validate();
    return this.isValid;
  }
}

export class ProvideIdentificationToPersonsForm {
  identifications: IdentificationForPersonForm[] = [];
  private readonly dispatch: (action: DecisionAction) => void;
  readonly attachmentType: AttachmentType;
  readonly decisionId: string;
  readonly followUp: FollowUp;

  constructor(
    dispatch: (action: DecisionAction) => void,
    attachmentType: AttachmentType,
    decisionId: string,
    followUp: FollowUp,
    persons: IdentificationForPerson[],
    locale: Language,
  ) {
    this.dispatch = dispatch;
    this.attachmentType = attachmentType;
    this.decisionId = decisionId;
    this.followUp = followUp;
    this.identifications = persons.map((p) => new IdentificationForPersonForm(p, locale));
    makeAutoObservable(this, {}, { autoBind: true });
  }

  validateAndSave() {
    if (this.identifications.every((i) => i.validate())) {
      const input = JSON.stringify(this.identifications.map((i) => i.toJSON()));
      const payload = new TextEncoder().encode(input);
      this.dispatch(
        uploadAttachmentsEvent({
          source: this.followUp,
          files: [
            new File([payload], "identifications.json", {
              type: "text/plain",
            }),
          ],
          attachmentType: this.attachmentType,
          reference: this.decisionId,
          overrideExistingAttachmentType: true,
        }),
      );
    }
  }
}

export class ProvideEmailToAttachmentForm {
  email: StringFieldModel;
  data: AttachmentFollowUp;
  readonly dispatch: DbotEventDispatcher;
  readonly attachmentType: AttachmentType;
  readonly decisionId: string;

  constructor(
    dispatch: DbotEventDispatcher,
    attachmentType: AttachmentType,
    decisionId: string,
    data: AttachmentFollowUp,
  ) {
    this.dispatch = dispatch;
    this.attachmentType = attachmentType;
    this.decisionId = decisionId;
    this.data = data;
    this.email = createEmailField({
      value: data.email,
      required: data.requiresEmail,
      onChange: (email) => this.save(email),
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  save(email: string | undefined) {
    const data: AttachmentFollowUp = {
      ...this.data,
      email: email,
    };
    this.dispatch(updateFollowUpStatusEvent(this.decisionId, data));
  }
}

function hasAllBoardMemberSSNs(context: ProjectContext) {
  const companyRoles = context.companyInfo.roles;
  const participants = context.boardMeetingNoticeRecipients;
  const participatingRoles = companyRoles.filter((r) =>
    participants.some((p) => p.id === r.companyroleId),
  );
  return participatingRoles.every((r) => r.socialSecurityNumber);
}

function hasExtraParticipants(context: ProjectContext) {
  const companyRoles = context.companyInfo.roles;
  const participants = context.boardMeetingNoticeRecipients;
  return participants.some((p) => !companyRoles.some((r) => p.id === r.companyroleId));
}

function isMissingShareholderSSN(context: ProjectContext) {
  return context.shareholderData.shareholders
    .filter((s) => hasShares(s))
    .filter((s) => isCountryCodeNorway(s.nationality) || isCountryCodeSweden(s.nationality))
    .some((s) =>
      s.isCompany ? !s.contactInfo?.socialSecurityNumber : !s.personInfo?.socialSecurityNumber,
    );
}

function hasForeignShareholders(context: ProjectContext) {
  return context.shareholderData.shareholders.some(
    (s) => !isCountryCodeNorway(s.nationality) && !isCountryCodeSweden(s.nationality),
  );
}

function findSharepointSettings(
  context: ProjectContext,
  action: OpenReviewDialogAction,
): SharepointSettingsForDocument | undefined {
  const result = context.documentSigners.get(action.document.documentId);
  if (isOk(result)) {
    return result.data.companySettings
      ?.find((i) => i.orgNo === context.orgNo)
      ?.sharepointSettings?.settings.find((s) => s.documentType === action.document.type);
  } else {
    return undefined;
  }
}
